// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving sold assets snapshot.
 *
 * Provides functionality to fetch sold assets snapshot based on a specific date.
 * Each response covers a single cost currency: the backend-computed summary stats
 * for that currency plus the assets denominated in it.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import {
  SoldAssetSnapshotItem,
  soldAssetSnapshotItemSchema,
} from '@/report/api/types';
import { parseArrayInChunks } from '@/utilities/chunked-parse';
import { DateTime } from 'luxon';
import { z } from 'zod';

/** Backend-computed summary stats for a single cost currency's sold-asset snapshot. */
export const soldAssetCurrencyStatsSchema = z.object({
  isoCode: isoCurrencyCodeSchema,
  assetCount: z.number(),
  assetsWithCompleteAccounting: z.number(),
  assetsWithIncompleteAccounting: z.number(),
  weightedInventoryDays: z.number().nullable(),
  weightedServiceDaysOnBooks: z.number().nullable(),
  weightedServiceDaysOffBooks: z.number().nullable(),
  totalSaleValue: z.string().nullable(),
  totalBookValueAtSale: z.string().nullable(),
  totalGainLossOnSale: z.string().nullable(),
});

export type SoldAssetCurrencyStats = z.infer<
  typeof soldAssetCurrencyStatsSchema
>;

// The stats live under the response envelope's data field; assets are stripped here
// and validated separately in chunks below.
const soldSnapshotStatsResponseSchema = z.object({
  data: soldAssetCurrencyStatsSchema,
  metadata: responseMetadataSchema,
});

/** A single currency's sold-asset snapshot: backend stats plus its assets. */
export interface SoldAssetsSnapshot extends SoldAssetCurrencyStats {
  assets: SoldAssetSnapshotItem[];
}

// The raw wire response keeps assets as an unparsed array so they can be validated
// in chunks (snapshots can be large); the stats are validated up front.
interface RawSoldAssetsSnapshotResponse {
  data: SoldAssetCurrencyStats & {
    assets: SoldAssetSnapshotItem[];
  };
}

interface SoldAssetsSnapshotParameters {
  asOfDate: DateTime;
  currency: IsoCurrencyCode;
}

/**
 * Retrieves a snapshot of sold assets as of a specific date for a single cost
 * currency.
 *
 * @param params - The parameters for the snapshot request
 * @returns A promise resolving to the single-currency sold-asset snapshot
 */
export async function getSoldAssetsSnapshot(
  params: SoldAssetsSnapshotParameters
): Promise<SoldAssetsSnapshot> {
  const asOfDateString = params.asOfDate.toISODate();

  const path = '/v1/report/asset/sold-snapshot';
  const response = await api.get<RawSoldAssetsSnapshotResponse>(path, {
    params: {
      asOfDate: asOfDateString,
      currency: params.currency,
    },
  });

  const parsed = await parseResponse(
    soldSnapshotStatsResponseSchema,
    response.data,
    { schemaName: 'SoldAssetsSnapshotStats', endpoint: path }
  );
  const stats = parsed.data;
  const assets = await parseArrayInChunks(
    response.data.data.assets,
    soldAssetSnapshotItemSchema,
    {
      schemaValidation: {
        schemaName: 'SoldAssetSnapshotItem',
        endpoint: path,
        extra: {
          asOfDate: asOfDateString,
          isoCode: stats.isoCode,
        },
      },
    }
  );
  return { ...stats, assets };
}
